// Zoho sync admin panel: status per module, manual trigger, grant-code exchange.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth-store";
import { ArrowLeft, RefreshCw, KeyRound, CheckCircle2, AlertTriangle, Play } from "lucide-react";

interface SyncState {
  id: string;
  module: string;
  lastSyncAt: string | null;
  lastSuccessAt: string | null;
  cursor: string | null;
  lastStatus: string | null;
  lastError: string | null;
  recordsSeen: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsSkipped: number;
  updatedAt: string;
}

interface Status {
  configured: boolean;
  accountsBase: string;
  apiBase: string;
  syncCron: string;
  modules: string[];
  states: SyncState[];
}

function fmtRel(iso: string | null) {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function AdminZohoPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";
  const [grant, setGrant] = useState("");
  const [healthResult, setHealthResult] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/zoho/status"],
    queryFn: async () => (await api.get("/zoho/status")).data as Status,
    refetchInterval: 10_000,
  });

  const exchangeMut = useMutation({
    mutationFn: async () => (await api.post("/zoho/exchange-grant", { code: grant.trim() })).data,
    onSuccess: () => { setGrant(""); qc.invalidateQueries({ queryKey: ["/zoho/status"] }); },
  });

  const healthMut = useMutation({
    mutationFn: async () => (await api.post("/zoho/health")).data as { ok: boolean; message: string },
    onSuccess: (r) => setHealthResult(`${r.ok ? "✓" : "✗"} ${r.message}`),
    onError: (err: any) => setHealthResult(`✗ ${err?.response?.data?.message ?? "health failed"}`),
  });

  const syncAllMut = useMutation({
    mutationFn: async () => (await api.post("/zoho/sync-now")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/zoho/status"] }),
  });

  const syncOneMut = useMutation({
    mutationFn: async (module: string) => (await api.post(`/zoho/sync-now/${module}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/zoho/status"] }),
  });

  const resetMut = useMutation({
    mutationFn: async (module: string) => (await api.post(`/zoho/reset-cursor/${module}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/zoho/status"] }),
  });

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Zoho Sync" />
        <div className="p-6 text-sm text-muted-foreground">You don't have permission to view this page.</div>
      </>
    );
  }

  const configured = data?.configured === true;

  return (
    <>
      <PageHeader
        title="Zoho Sync (one-way)"
        description="Mirror Accounts, Contacts, Leads, and Deals from Zoho CRM into CBC once a week."
        actions={<Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="mr-1 h-4 w-4"/>Back to Admin</Link></Button>}
      />

      <div className="p-6 space-y-4">
        {/* Config status */}
        <Card>
          <CardHeader><CardTitle className="text-base">Connection</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              {configured
                ? <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3"/>Configured</Badge>
                : <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3"/>Not configured</Badge>}
              <span className="text-muted-foreground">
                DC: <code className="rounded bg-muted px-1 py-0.5">{data?.apiBase ?? "—"}</code> · schedule: <code className="rounded bg-muted px-1 py-0.5">{data?.syncCron ?? "—"}</code>
              </span>
            </div>

            {!configured && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                <div className="mb-2 font-medium">One-time setup — exchange a grant code for a permanent refresh token</div>
                <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                  <li>Open <a className="text-primary hover:underline" href="https://api-console.zoho.in/" target="_blank" rel="noreferrer">api-console.zoho.in</a> → your Self Client → <span className="font-medium">Generate Code</span> tab.</li>
                  <li>Scope: <code className="rounded bg-muted px-1 py-0.5">ZohoCRM.modules.ALL.READ,ZohoCRM.users.READ</code> · Time: 10 min · any description.</li>
                  <li>Click Create — copy the code that appears (starts with <code>1000.</code>).</li>
                  <li>Paste it below within 10 minutes and hit Exchange. The refresh token is saved server-side; backend restarts automatically.</li>
                </ol>
              </div>
            )}

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="grant">Grant code (from Zoho Generate Code)</Label>
                <Input id="grant" value={grant} onChange={(e) => setGrant(e.target.value)} placeholder="1000.abcdef…" autoComplete="off" />
              </div>
              <Button onClick={() => exchangeMut.mutate()} disabled={!grant.trim() || exchangeMut.isPending}>
                <KeyRound className="mr-1 h-4 w-4" />{exchangeMut.isPending ? "Exchanging…" : "Exchange"}
              </Button>
            </div>
            {exchangeMut.isSuccess && (
              <p className="rounded-md bg-primary/10 px-3 py-2 text-xs">
                Refresh token saved to <code>backend/.env</code>. <span className="font-medium">Restart the backend</span> (pm2 restart cbc-crm) so it reads the new token, then click <span className="font-medium">Test connection</span>.
              </p>
            )}
            {exchangeMut.isError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {(exchangeMut.error as any)?.response?.data?.message ?? "Exchange failed. Grant codes expire in 10 min and are single-use — generate a fresh one."}
              </p>
            )}

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => healthMut.mutate()} disabled={!configured || healthMut.isPending}>
                {healthMut.isPending ? "Pinging…" : "Test connection"}
              </Button>
              {healthResult && <span className="text-xs text-muted-foreground">{healthResult}</span>}
            </div>
          </CardContent>
        </Card>

        {/* Big obvious sync button — outside the Card so it's never missed */}
        <div className="flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={() => syncAllMut.mutate()} disabled={!configured || syncAllMut.isPending}>
            <Play className="mr-2 h-5 w-5" />{syncAllMut.isPending ? "Syncing all modules…" : "Sync all now"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Runs Accounts → Contacts → Leads → Deals in order. First run pulls everything; subsequent runs pull only changes since the last cursor.
          </span>
        </div>

        {/* Per-module status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Module status</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>}
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Module</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Last run</th>
                  <th className="px-3 py-2 text-right font-medium">Seen</th>
                  <th className="px-3 py-2 text-right font-medium">Created</th>
                  <th className="px-3 py-2 text-right font-medium">Updated</th>
                  <th className="px-3 py-2 text-right font-medium">Skipped</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.modules ?? []).map((m) => {
                  const s = data?.states.find((x) => x.module === m);
                  return (
                    <tr key={m} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{m}</td>
                      <td className="px-3 py-2">
                        {s?.lastStatus === "success" && <Badge variant="default">OK</Badge>}
                        {s?.lastStatus === "running" && <Badge variant="secondary">Running</Badge>}
                        {s?.lastStatus === "error"   && <Badge variant="destructive">Error</Badge>}
                        {!s?.lastStatus && <span className="text-xs text-muted-foreground">never run</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {fmtRel(s?.lastSyncAt ?? null)}
                        {s?.lastError && <div className="text-destructive">↳ {s.lastError.slice(0, 120)}</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{s?.recordsSeen ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s?.recordsCreated ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s?.recordsUpdated ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s?.recordsSkipped ?? 0}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => syncOneMut.mutate(m)} disabled={!configured || syncOneMut.isPending}>
                          <Play className="mr-1 h-3 w-3" />Sync
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Reset cursor for ${m}? Next sync will pull everything again.`)) resetMut.mutate(m); }} disabled={!configured}>
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          This is a one-way mirror: Zoho is the source of truth. CBC never pushes changes back to Zoho. Deletions in Zoho do <span className="font-medium">not</span> cascade — records stay here. Custom Zoho fields are ignored.
        </p>
      </div>
    </>
  );
}
